import { NavLink, useNavigate } from 'react-router-dom'
import {
  Cpu,
  History,
  LogOut,
  Radio,
  Scan,
  ScanLine,
  Tag,
  User,
  X,
  ChevronRight,
} from 'lucide-react'
import leapFaviconWhite from '@/assets/leap-favicon-white.png'
import { LanguageToggle } from '@/components/i18n/LanguageToggle'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { BRAND } from '@/constants/brand'
import { useRfidDrawer } from '@/contexts/RfidDrawerContext'
import { useLocale } from '@/i18n/LocaleProvider'
import { useAuthStore } from '@/stores/authStore'
import { displayUserName, cn } from '@/utils/cn'
import { canAccessRfidEngineAdmin } from '@/utils/rfidAccess'

export function RfidMobileSidebar() {
  const { open, closeDrawer } = useRfidDrawer()
  const { user, logout } = useAuthStore()
  const { t, isRtl } = useLocale()
  const navigate = useNavigate()

  if (!open) return null

  const displayName = displayUserName(user) || t.sidebar?.signedIn || 'Signed in'
  const secondary = user?.email && user?.name ? user.email : user?.userId ? String(user.userId) : ''

  const go = (path: string) => {
    closeDrawer()
    navigate(path)
  }

  const onLogout = async () => {
    closeDrawer()
    await logout()
    navigate('/login', { replace: true })
  }

  const tools = [
    {
      to: '/scanner',
      icon: ScanLine,
      title: t.sidebar?.scannerConnect ?? t.App.scanner,
      subtitle: t.sidebar?.scannerConnectSub ?? '',
    },
    {
      to: '/raw-scan',
      icon: Radio,
      title: t.sidebar?.rawScan ?? t.App.rawScan,
      subtitle: t.sidebar?.rawScanSub ?? '',
    },
    {
      to: '/recent-scans',
      icon: History,
      title: t.sidebar?.recentScans ?? t.App.recentScans,
      subtitle: t.sidebar?.recentScansSub ?? '',
    },
    {
      to: '/scanner/info',
      icon: Cpu,
      title: t.sidebar?.scannerInfo ?? t.App.scannerInfo,
      subtitle: t.sidebar?.scannerInfoSub ?? '',
    },
    ...(canAccessRfidEngineAdmin(user)
      ? [
          {
            to: '/engine',
            icon: Tag,
            title: t.sidebar?.engine ?? t.App.engine ?? 'RFID Engine',
            subtitle: t.sidebar?.engineSub ?? 'Birth, tag, lookup',
          },
        ]
      : []),
  ]

  return (
    <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/45"
        aria-label={t.sidebar?.closeMenu ?? 'Close menu'}
        onClick={closeDrawer}
      />

      <aside
        className={cn(
          'absolute inset-y-0 flex w-[min(82vw,320px)] flex-col border-border bg-card text-foreground shadow-2xl',
          isRtl ? 'right-0 border-l' : 'left-0 border-r'
        )}
        style={{
          animation: isRtl
            ? 'rfidDrawerInRtl 280ms cubic-bezier(0.22,1,0.36,1)'
            : 'rfidDrawerInLtr 280ms cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3.5 pt-[max(0.875rem,env(safe-area-inset-top))]">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
            style={{ background: BRAND }}
          >
            <img
              src={leapFaviconWhite}
              alt=""
              className="h-[22px] w-[22px] rounded object-contain"
              draggable={false}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-black text-foreground">
              {t.App.name}
            </div>
            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t.App.subtitle}
            </div>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"
            aria-label={t.sidebar?.closeMenu ?? 'Close menu'}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          <section>
            <div className="rfid-section-label mb-2 px-1">{t.sidebar?.profile ?? t.App.profile}</div>
            <button
              type="button"
              onClick={() => go('/profile')}
              className="flex w-full items-center gap-3 rounded-xl border border-border bg-muted/40 px-3 py-3 text-left"
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
                style={{ background: BRAND }}
              >
                <User className="h-[18px] w-[18px]" strokeWidth={2} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-extrabold text-foreground">
                  {displayName}
                </span>
                {secondary ? (
                  <span className="mt-0.5 block truncate text-xs font-semibold text-muted-foreground">
                    {secondary}
                  </span>
                ) : null}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </section>

          <section>
            <button
              type="button"
              onClick={() => go('/')}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-white shadow-sm"
              style={{ background: BRAND }}
            >
              <Scan className="h-[22px] w-[22px] shrink-0" strokeWidth={2.4} />
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-black">
                  {t.sidebar?.scan ?? t.App.home}
                </span>
                <span className="mt-0.5 block text-[11px] font-semibold text-white/80">
                  {t.sidebar?.scanSub ?? t.Home.subtitle}
                </span>
              </span>
            </button>
          </section>

          <section>
            <div className="rfid-section-label mb-2 px-1">{t.sidebar?.tools ?? 'Tools'}</div>
            <div className="space-y-1.5">
              {tools.map(({ to, icon: Icon, title, subtitle }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={closeDrawer}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors',
                      isActive
                        ? 'border-primary/40 bg-primary-50'
                        : 'border-border bg-card hover:bg-muted/50'
                    )
                  }
                >
                  <Icon className="h-[18px] w-[18px] shrink-0" style={{ color: BRAND }} strokeWidth={2} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-extrabold text-foreground">{title}</span>
                    {subtitle ? (
                      <span className="mt-0.5 block text-[11px] font-semibold text-muted-foreground">
                        {subtitle}
                      </span>
                    ) : null}
                  </span>
                </NavLink>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <div className="rfid-section-label mb-2 px-1">{t.sidebar?.options ?? 'Options'}</div>
            <ThemeSwitcher variant="surface" className="w-full" />
            <LanguageToggle />
            <button
              type="button"
              onClick={() => void onLogout()}
              className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-extrabold text-foreground hover:bg-muted/50"
            >
              <LogOut className="h-[18px] w-[18px]" style={{ color: BRAND }} strokeWidth={2} />
              {t.sidebar?.logout ?? t.App.signOut}
            </button>
          </section>
        </div>

        <div className="border-t border-border px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-center">
          <div className="text-[11px] font-semibold text-muted-foreground">
            {t.sidebar?.footerNote ?? 'LEAP India · RFID Operations'}
          </div>
        </div>
      </aside>
    </div>
  )
}
